import React, { useEffect, useRef } from 'react';
import './CosmicField.scss';

// ============================================================================
// CosmicField — AI-inspired ambient background
//
// A living canvas of golden particles that form "neural" ambient lines as they
// drift. The cursor becomes a soft light-field that gently attracts particles
// and brightens nearby connections, giving the page an interactive, alive feel.
//
// Props:
//   density   - number of particles (default 60)
//   speed     - base drift speed (default 1)
//   linkDist  - distance under which particles connect (default 130)
//   className - extra classes
//
// The effect is fully disabled for prefers-reduced-motion and touch devices
// (static glow only), so it never causes discomfort or jank on mobile.
// ============================================================================
const CosmicField = ({ density = 60, speed = 1, linkDist = 130, className = '' }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let raf = 0;
    let running = !reduceMotion;
    let cursor = null; // { x, y }
    let glowGrad = null;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      glowGrad = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) * 0.6
      );
      glowGrad.addColorStop(0, 'rgba(229, 162, 60, 0.05)');
      glowGrad.addColorStop(1, 'rgba(229, 162, 60, 0)');
    };

    // Particles are born from the bottom and gently float upward with drift.
    // Cap the pair-pass workload: 46 particles is ~1035 distance checks/frame,
    // which stays smooth on mid-range desktops without changing the look.
    const effDensity = Math.max(20, Math.min(density, 46));
    const particles = Array.from({ length: effDensity }, (_, i) => ({
      x: Math.random() * (canvas.clientWidth || window.innerWidth),
      y: Math.random() * (canvas.clientHeight || window.innerHeight),
      vx: (Math.random() - 0.5) * 0.18 * speed,
      vy: -(0.05 + Math.random() * 0.22) * speed,
      r: 0.6 + Math.random() * 1.6,
      pulse: Math.random() * Math.PI * 2,
      pulseSpeed: 0.01 + Math.random() * 0.03,
      pull: Math.random() * 0.02, // how strongly the light-field attracts it
    }));

    // Position of the pointer in canvas space.
    const setCursor = (x, y) => {
      const rect = canvas.getBoundingClientRect();
      cursor = { x: x - rect.left, y: y - rect.top };
    };

    const onPointerMove = (e) => {
      if (!finePointer || reduceMotion) return;
      setCursor(e.clientX, e.clientY);
    };
    const onPointerLeave = () => { cursor = null; };
    const onTouchStart = () => { cursor = null; };

    const drawStatic = () => {
      // One gentle frame — used for reduced motion / no fine pointer.
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(243, 217, 164, 0.28)';
        ctx.fill();
      });
    };

    const tick = () => {
      ctx.clearRect(0, 0, width, height);
      const now = Date.now();

      // Gentle global glow behind everything (gradient cached on resize)
      ctx.save();
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();

      // Light-field around the cursor
      if (cursor && finePointer) {
        ctx.save();
        const cg = ctx.createRadialGradient(cursor.x, cursor.y, 0, cursor.x, cursor.y, 190);
        cg.addColorStop(0, 'rgba(255, 224, 165, 0.16)');
        cg.addColorStop(1, 'rgba(255, 224, 165, 0)');
        ctx.fillStyle = cg;
        ctx.fillRect(0, 0, width, height);
        ctx.restore();
      }

      // Update + draw particles (no shadowBlur — on a canvas that re-rasterizes
      // per shadowed arc, which dominates frame cost; the glow gradient above
      // already supplies the soft ambience).
      particles.forEach((p) => {
        // gentle attraction to the cursor light-field
        if (cursor && finePointer) {
          const dx = cursor.x - p.x;
          const dy = cursor.y - p.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < 36000) {
            const d = Math.sqrt(d2) || 1;
            p.vx += (dx / d) * p.pull * 0.2;
            p.vy += (dy / d) * p.pull * 0.2;
          }
        }
        // soft damping so particles don't accelerate forever
        p.vx *= 0.99;
        p.vy *= 0.99;
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += p.pulseSpeed;

        // wrap around edges
        if (p.y < -20) { p.y = height + 20; p.x = Math.random() * width; }
        if (p.y > height + 20) { p.y = -20; p.x = Math.random() * width; }
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;

        const a = 0.28 + 0.45 * (0.5 + 0.5 * Math.sin(p.pulse));
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(243, 217, 164, ${a})`;
        ctx.fill();
      });

      // Neural ambient connection lines
      ctx.lineWidth = 0.6;
      const linkDistSq = linkDist * linkDist;
      for (let i = 0; i < particles.length; i += 1) {
        for (let j = i + 1; j < particles.length; j += 1) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDistSq) {
            const d = Math.sqrt(d2) || 1;
            let alpha = (1 - d / linkDist) * 0.14;
            // brighten near the cursor light-field
            if (cursor && finePointer) {
              const mx = (a.x + b.x) / 2;
              const my = (a.y + b.y) / 2;
              const cd = Math.sqrt((mx - cursor.x) ** 2 + (my - cursor.y) ** 2);
              if (cd < 190) alpha += 0.2 * (1 - cd / 190);
            }
            ctx.strokeStyle = `rgba(229, 162, 60, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      void now;
      raf = requestAnimationFrame(tick);
    };

    resize();
    const animate = !!running && finePointer;

    // Defer the 60fps loop until the main thread goes idle. The hero's LCP
    // (title text) must paint before a canvas burns the CPU — so we draw one
    // gentle static frame right away and only start animating after a rAF
    // quiescence / requestIdleCallback.
    let started = false;
    let loopHandle = 0;
    const beginLoop = () => {
      if (started) return;
      started = true;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    if (animate) {
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerleave', onPointerLeave);
      window.addEventListener('touchstart', onTouchStart, { passive: true });
      drawStatic();
      const startDeferred = () => beginLoop();
      if (typeof requestIdleCallback === 'function') {
        loopHandle = window.requestIdleCallback(startDeferred, { timeout: 1200 });
      } else {
        loopHandle = window.setTimeout(startDeferred, 600);
      }
    } else {
      drawStatic();
    }

    // Pause the expensive per-frame loop while the canvas is off-screen so it
    // doesn't keep burning CPU (and causing scroll jank) in the background.
    let visible = true;
    const start = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    const stop = () => cancelAnimationFrame(raf);
    const io =
      animate && 'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              const isVisible = entries[0].isIntersecting;
              if (isVisible && !visible) start();
              if (!isVisible && visible) stop();
              visible = isVisible;
            },
            { rootMargin: '40px' }
          )
        : null;
    if (io) io.observe(canvas);

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      if (typeof requestIdleCallback === 'function') cancelIdleCallback(loopHandle);
      else clearTimeout(loopHandle);
      if (io) io.disconnect();
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('touchstart', onTouchStart);
    };
  }, [density, speed, linkDist]);

  return <canvas ref={canvasRef} className={`cosmic-field ${className}`} aria-hidden="true" />;
};

export default CosmicField;
